import React from 'react';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { Tag, Modal, Dropdown, Tooltip } from 'antd';
import {
    PlusOutlined,
    ExclamationCircleOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
} from '@ant-design/icons';
import Table from '@/components/ui/table/Table';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { useProjectTemplates } from '@/hooks/projects/useProjectTemplates';
import ProjectTemplateModal from '@/components/module/projects/project_templates/ProjectTemplateModal';

const { confirm } = Modal;

export default function ProjectTemplates() {
    const {
        templates,
        isLoading,
        modalOpen,
        editingTemplate,
        form,
        openCreateModal,
        openEditModal,
        closeModal,
        handleSubmit,
        canManage,
        isSaving,
        deleteTemplate,
    } = useProjectTemplates();

    const [isViewMode, setIsViewMode] = React.useState(false);
    const [contextRecord, setContextRecord] = React.useState(null);
    const [contextVisible, setContextVisible] = React.useState(false);
    const [contextPosition, setContextPosition] = React.useState({ x: 0, y: 0 });

    const openViewModal = (template) => {
        setIsViewMode(true);
        openEditModal(template);
    };

    const handleOpenEditModal = (template) => {
        setIsViewMode(false);
        openEditModal(template);
    };

    const handleCloseModal = () => {
        closeModal();
        setIsViewMode(false);
    };

    const handleOpenCreateModal = () => {
        setIsViewMode(false);
        openCreateModal();
    };

    const showDeleteConfirm = (template) => {
        confirm({
            title: `Delete template "${template.name}"?`,
            icon: <ExclamationCircleOutlined style={{ color: '#ef4444' }} />,
            content: 'Are you sure you want to delete this project template? This action cannot be undone.',
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk() {
                deleteTemplate(template.id);
            },
        });
    };

    const columns = [
        {
            title: 'Template',
            key: 'details',
            width: '22%',
            render: (_, record) => (
                <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--ant-color-text)', lineHeight: '1.4' }}>
                    {record.name}
                </span>
            ),
        },
        {
            title: 'Description',
            key: 'description',
            width: '30%',
            render: (_, record) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{
                        fontSize: '11px',
                        color: '#7543DC',
                        fontFamily: 'monospace',
                        background: 'rgba(117,67,220,0.07)',
                        borderRadius: '4px',
                        padding: '1px 6px',
                        display: 'inline-block',
                        width: 'fit-content',
                        fontWeight: 500,
                        letterSpacing: '0.3px',
                    }}>
                        {record.code}
                    </span>
                    {record.description ? (
                        <span style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)', lineHeight: '1.5' }}>
                            {record.description}
                        </span>
                    ) : (
                        <span style={{ fontSize: '12px', color: 'var(--ant-color-text-quaternary)' }}>—</span>
                    )}
                </div>
            ),
        },
        {
            title: 'Capabilities',
            key: 'capabilities',
            width: '23%',
            render: (_, record) => {
                const caps = [];
                if (record.usePhases) caps.push({ label: 'Phase', color: 'blue' });
                if (record.useMilestones) caps.push({ label: 'Milestone', color: 'green' });
                if (record.useSprints) caps.push({ label: 'Sprint', color: 'gold' });
                if (record.useTasks) caps.push({ label: 'Task', color: 'purple' });
                if (record.useSubmissionMemo) caps.push({ label: 'Submission Memo', color: 'magenta' });

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {caps.length === 0 ? (
                            <span style={{ fontSize: '12px', color: 'var(--ant-color-text-secondary)' }}>Standard (Blank)</span>
                        ) : (
                            caps.map((c) => (
                                <Tag key={c.label} color={c.color} bordered={false} style={{ fontSize: '11px', fontWeight: 500 }}>
                                    {c.label}
                                </Tag>
                            ))
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Review Mode',
            dataIndex: 'submissionMode',
            key: 'submissionMode',
            width: '15%',
            render: (mode) => {
                const label =
                    mode === 'formal'
                        ? 'Formal Client Submission'
                        : mode === 'memo'
                        ? 'Client Review Memo'
                        : mode === 'internal'
                        ? 'Internal Workspace Approval'
                        : 'No Client Approval';
                return (
                    <span style={{ fontSize: '13px', fontWeight: 500 }}>
                        {label}
                    </span>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: '10%',
            align: 'center',
            render: (_, record) => (
                <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Tooltip title="View" placement="top">
                        <button
                            onClick={() => openViewModal(record)}
                            style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                width: 30, height: 30,
                                borderRadius: 8,
                                border: '1px solid transparent',
                                background: 'transparent',
                                color: 'var(--ant-color-text-tertiary)',
                                cursor: 'pointer',
                                transition: 'all 0.18s ease',
                                fontSize: 14,
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.color = '#6366f1';
                                e.currentTarget.style.border = '1px solid #6366f1';
                                e.currentTarget.style.background = 'rgba(99,102,241,0.06)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--ant-color-text-tertiary)';
                                e.currentTarget.style.border = '1px solid transparent';
                                e.currentTarget.style.background = 'transparent';
                            }}
                        >
                            <EyeOutlined />
                        </button>
                    </Tooltip>

                    {canManage && (
                        <Tooltip title="Edit" placement="top">
                            <button
                                onClick={() => handleOpenEditModal(record)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 30, height: 30,
                                    borderRadius: 8,
                                    border: '1px solid transparent',
                                    background: 'transparent',
                                    color: 'var(--ant-color-text-tertiary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.18s ease',
                                    fontSize: 14,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#f59e0b';
                                    e.currentTarget.style.border = '1px solid #f59e0b';
                                    e.currentTarget.style.background = 'rgba(245,158,11,0.06)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--ant-color-text-tertiary)';
                                    e.currentTarget.style.border = '1px solid transparent';
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <EditOutlined />
                            </button>
                        </Tooltip>
                    )}

                    {canManage && (
                        <Tooltip title="Delete" placement="top">
                            <button
                                onClick={() => showDeleteConfirm(record)}
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    width: 30, height: 30,
                                    borderRadius: 8,
                                    border: '1px solid transparent',
                                    background: 'transparent',
                                    color: 'var(--ant-color-text-tertiary)',
                                    cursor: 'pointer',
                                    transition: 'all 0.18s ease',
                                    fontSize: 14,
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.color = '#ef4444';
                                    e.currentTarget.style.border = '1px solid #ef4444';
                                    e.currentTarget.style.background = 'rgba(239,68,68,0.06)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.color = 'var(--ant-color-text-tertiary)';
                                    e.currentTarget.style.border = '1px solid transparent';
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                <DeleteOutlined />
                            </button>
                        </Tooltip>
                    )}
                </div>
            ),
        },
    ];

    return (
        <ListBodyLayout
            title="Project Templates"
            description="Manage configurations and workflows for new workspace projects"
            actions={null}
        >
            <Container $display="flex" $flexDirection="column" $gap="24px" $marginTop="24px">
                <Table
                    columns={columns}
                    dataSource={templates}
                    loading={isLoading}
                    pagination={true}
                    onRow={(record) => ({
                        onClick: () => openViewModal(record),
                        onContextMenu: (e) => {
                            e.preventDefault();
                            setContextRecord(record);
                            setContextPosition({ x: e.clientX, y: e.clientY });
                            setContextVisible(true);
                        },
                        style: { cursor: 'pointer' },
                    })}
                />
            </Container>

            {contextVisible && (
                <Dropdown
                    open={contextVisible}
                    onOpenChange={(v) => setContextVisible(v)}
                    menu={{
                        items: [
                            { key: 'view', label: 'View' },
                            { key: 'edit', label: 'Edit', disabled: !canManage },
                            { key: 'delete', label: 'Delete', danger: true, disabled: !canManage },
                        ],
                        onClick: ({ key }) => {
                            setContextVisible(false);
                            if (key === 'view') {
                                openViewModal(contextRecord);
                            } else if (key === 'edit') {
                                handleOpenEditModal(contextRecord);
                            } else if (key === 'delete') {
                                showDeleteConfirm(contextRecord);
                            }
                        },
                    }}
                >
                    <div
                        style={{
                            position: 'fixed',
                            left: contextPosition.x,
                            top: contextPosition.y,
                            width: 1,
                            height: 1,
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}
                    />
                </Dropdown>
            )}

            <ProjectTemplateModal
                open={modalOpen}
                onCancel={handleCloseModal}
                onSubmit={handleSubmit}
                form={form}
                isSaving={isSaving}
                isEdit={!!editingTemplate && !isViewMode}
                isView={isViewMode}
            />
        </ListBodyLayout>
    );
}
